import { Component, Injector, OnInit } from '@angular/core';
import { ApAlertService } from 'webcommon/core-ui';
import { FcFilterConfigurationFieldType, FcGenericFilterModalBaseComponent } from '../../generic/index';
import { FormControl, FormGroup } from '@angular/forms';
import { FindControlIdService } from 'webcommon/legacy-common';
import {
    ApCustomDateRange,
    FindControlAdditionalSearchParametersDto,
    FindControlAdditionalSearchParametersType,
    FindControlRequiredSearchParametersDto,
    Guid,
    GuidStrValue
} from 'webcommon/shared';

@Component({
    providers: [ApAlertService],
    selector: 'fc-patient-result-filter-modal',
    templateUrl: './fc-patient-result-filter-modal.component.html'
})
export class FcPatientResultFilterModalComponent extends FcGenericFilterModalBaseComponent implements OnInit {
    public override baseId = 'fc-patient-result-filter';

    private findControlResetTimer: ReturnType<typeof setTimeout> | null = null;

    private readonly defaultFindControlIds = {
        attachmentType: FindControlIdService.ListAttachmentTypeViewSecurity,
        resultStatus: FindControlIdService.ListObservationOrderResultStatus,
        urgency: FindControlIdService.ListMessageUrgency,
        provider: FindControlIdService.ViewProvider,
        patient: this.findControlIdService.ViewFindPatient,
        dateType: this.findControlIdService.ListMisc
    };

    protected attachmentFindControlId = this.defaultFindControlIds.attachmentType;
    protected resultStatusFindControlId = this.defaultFindControlIds.resultStatus;
    protected urgencyFindControlId = this.defaultFindControlIds.urgency;
    protected providerFindControlId = this.defaultFindControlIds.provider;
    protected patientFindControlId = this.defaultFindControlIds.patient;
    protected dateTypeFindControlId = this.defaultFindControlIds.dateType;
    protected dateRangeFindControlId = this.findControlIdService.ListTimespan;
    protected dateRangePkId: string | null = null;

    public customDateRange: ApCustomDateRange = {};

    public dateTypeSearchParameters = {
        Type: FindControlRequiredSearchParametersDto,
        RequiredSearchParameters: [
            {
                ColumnName: 'ListType',
                Negate: false,
                Value: 'ResultTrackingDateType',
            },
        ],
    };

    protected nameControl = new FormControl<string | null>(null);
    protected attachmentTypeControl = new FormControl<GuidStrValue[]>([], { nonNullable: true });
    protected patientControl = new FormControl<GuidStrValue[]>([], { nonNullable: true });
    protected providerControl = new FormControl<GuidStrValue[]>([], { nonNullable: true });
    protected resultStatusControl = new FormControl<GuidStrValue[]>([], { nonNullable: true });
    protected urgencyControl = new FormControl<GuidStrValue[]>([], { nonNullable: true });
    protected dateTypeControl = new FormControl<GuidStrValue[]>([], { nonNullable: true });
    protected dateRangeControl = new FormControl<string | null>(null);

    public form = new FormGroup<PatientResultFilterFormType>({
        name: this.nameControl,
        attachmentType: this.attachmentTypeControl,
        patient: this.patientControl,
        provider: this.providerControl,
        resultStatus: this.resultStatusControl,
        urgency: this.urgencyControl,
        dateType: this.dateTypeControl,
        dateRange: this.dateRangeControl
    });

    private createEmptyFormValue(): EmptyFormValue {
        return {
            name: null,
            attachmentType: [],
            resultStatus: [],
            urgency: [],
            provider: [],
            patient: [],
            dateType: [],
            dateRange: null
        };
    }

    constructor(
        protected injector: Injector,
        private findControlIdService: FindControlIdService
    ) {
        super(injector);
        this.initializeConfiguration();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.restorePreviousValues();
    }

    private restorePreviousValues(): void {
        const previous = this.fcFilterModalContext.previousSearchParameters;
        if (!previous) return;

        this.customDateRange = {};
        this.dateRangePkId = null;

        for (const param of previous.AdditionalSearchParameters) {
            const value = param.Value;

            if (param.ColumnName === PatientResultsFilterColumnName.DateRangeUid) {
                const guid = value ? Guid._coerceToGuidStr(value) : null;
                this.dateRangePkId = guid;
                this.dateRangeControl.setValue(guid);
            } else if (param.ColumnName === 'StartDate') {
                const parsed = this.parseDate(value);
                if (parsed) this.customDateRange.StartDate = parsed;
            } else if (param.ColumnName === 'EndDate') {
                const parsed = this.parseDate(value);
                if (parsed) this.customDateRange.EndDate = parsed;
            }
        }
    }

    private parseDate(value: string | null | undefined): Date | undefined {
        if (!value) return undefined;
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    }

    private initializeConfiguration(): void {
        const singleFields = [
            [PatientResultsFilterColumnName.Description, this.nameControl],
            [PatientResultsFilterColumnName.DateRangeUid, this.dateRangeControl]
        ] as const;

        const multipleFields = [
            [PatientResultsFilterColumnName.PatientUid, this.patientControl],
            [PatientResultsFilterColumnName.AttachmentTypeUid, this.attachmentTypeControl],
            [PatientResultsFilterColumnName.ObservationOrderResultStatusUid, this.resultStatusControl],
            [PatientResultsFilterColumnName.ProviderUid, this.providerControl],
            [PatientResultsFilterColumnName.MessageUrgencyUid, this.urgencyControl],
            [PatientResultsFilterColumnName.DateTypeUid, this.dateTypeControl]
        ] as const;

        singleFields.forEach(([columnName, control]) => {
            this.config.set(columnName, {
                type: FcFilterConfigurationFieldType.Single,
                control
            });
        });

        multipleFields.forEach(([columnName, control]) => {
            this.config.set(columnName, {
                type: FcFilterConfigurationFieldType.Multiple,
                control
            });
        });
    }

    protected onTimespanChanged(timeSpanId: string): void {
        this.dateRangePkId = timeSpanId;
        this.dateRangeControl.setValue(timeSpanId);
        this.customDateRange = {};
    }

    public override onClear(): void {
        this.form.reset(this.createEmptyFormValue(), { emitEvent: true });
        this.dateRangePkId = null;
        this.customDateRange = {};
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.form.updateValueAndValidity({ emitEvent: true });
        this.resetFindControlIds();
    }

    private resetFindControlIds(): void {
        this.setFindControlIds('');

        if (this.findControlResetTimer) {
            clearTimeout(this.findControlResetTimer);
        }

        this.findControlResetTimer = setTimeout(() => {
            this.findControlResetTimer = null;
            this.restoreFindControlIds();
        }, 0);
    }

    private setFindControlIds(value: string): void {
        this.attachmentFindControlId = value;
        this.resultStatusFindControlId = value;
        this.urgencyFindControlId = value;
        this.providerFindControlId = value;
        this.patientFindControlId = value;
        this.dateTypeFindControlId = value;
    }

    private restoreFindControlIds(): void {
        this.attachmentFindControlId = this.defaultFindControlIds.attachmentType;
        this.resultStatusFindControlId = this.defaultFindControlIds.resultStatus;
        this.urgencyFindControlId = this.defaultFindControlIds.urgency;
        this.providerFindControlId = this.defaultFindControlIds.provider;
        this.patientFindControlId = this.defaultFindControlIds.patient;
        this.dateTypeFindControlId = this.defaultFindControlIds.dateType;
    }

    public override save(): void {
        this.onClose(this.buildUserSearchParameters());
    }

    private buildUserSearchParameters(): FindControlAdditionalSearchParametersDto {
        const params: FindControlAdditionalSearchParametersDto = {
            Type: FindControlAdditionalSearchParametersType.MultipleValues,
            AdditionalSearchParameters: []
        };

        for (const [columnName, field] of this.config) {
            const rawValue = field.control.value;
            if (rawValue == null) continue;

            if (typeof rawValue === 'string') {
                this.addSearchParameter(params, columnName, rawValue);
            } else if (Array.isArray(rawValue)) {
                rawValue.forEach(value => value && this.addSearchParameter(params, columnName, value));
            }
        }

        this.appendCustomDateRange(params);
        return params;
    }

    private addSearchParameter(
        params: FindControlAdditionalSearchParametersDto,
        columnName: string,
        value: string | null
    ): void {
        if (value) {
            params.AdditionalSearchParameters.push({ ColumnName: columnName, Value: value });
        }
    }

    private appendCustomDateRange(params: FindControlAdditionalSearchParametersDto): void {
        const format = (date: Date | undefined): string | null =>
            date ? date.toISOString() : null;

        this.addSearchParameter(params, 'StartDate', format(this.customDateRange.StartDate));
        this.addSearchParameter(params, 'EndDate', format(this.customDateRange.EndDate));
    }
}

interface EmptyFormValue {
    name: string | null;
    attachmentType: string[];
    resultStatus: string[];
    urgency: string[];
    provider: string[];
    patient: string[];
    dateType: string[];
    dateRange: string | null;
}

export interface PatientResultFilterFormType {
    name: FormControl<string | null>;
    attachmentType: FormControl<string[]>;
    resultStatus: FormControl<string[]>;
    urgency: FormControl<string[]>;
    provider: FormControl<string[]>;
    patient: FormControl<string[]>;
    dateType: FormControl<string[]>;
    dateRange: FormControl<string | null>;
}

export enum PatientResultsFilterColumnName {
    Description = 'Description',
    ObservationOrderResultStatusUid = 'ObservationOrderResultStatusUid',
    PatientUid = 'PatientUid',
    AttachmentTypeUid = 'AttachmentTypeUid',
    MessageUrgencyUid = 'MessageUrgencyUid',
    ProviderUid = 'ProviderUid',
    DateTypeUid = 'DateTypeUid',
    DateRangeUid = 'DateRangeUid'
}
